import { lazy, type ComponentType } from 'react';

/* ==========================================================================
   lazyWithReload — survives a deploy that happened while the tab was open

   Builds emit content-hashed chunks. A tab loaded before a deploy still holds
   the old asset manifest in memory, so navigating to a not-yet-loaded route
   requests a chunk the server no longer has. The dynamic import rejects and
   the router dead-ends on an error screen.

   Recovery is a single reload: index.html is served with no-cache, so the tab
   picks up the current manifest and lands on the same URL.

   Twin file: apps/menu/src/lib/lazy-with-reload.ts — keep both in sync.
   ========================================================================== */

const RELOAD_KEY = 'vapt:chunk-reload-at';

/**
 * How long a reload attempt suppresses further attempts. Long enough to cover
 * the reload round-trip, short enough that a later, unrelated failure can still
 * recover on its own.
 */
const RELOAD_COOLDOWN_MS = 15_000;

/**
 * Matches the browser messages for a chunk that could not be fetched.
 * Chrome/Edge, Firefox and Safari each word it differently.
 *
 * The last one is Vite's own, not a browser's: when the chunk has CSS, the
 * preload helper fails on the stylesheet first and throws "Unable to preload
 * CSS for <url>". Missing it left a tab on "Unexpected Application Error!"
 * in staging on 23/09/2026, a minute after a panel deploy.
 */
const CHUNK_ERROR_PATTERN =
  /dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Unable to preload CSS/i;

export function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error && error.name === 'ChunkLoadError') return true;
  const message = error instanceof Error ? error.message : String(error);
  return CHUNK_ERROR_PATTERN.test(message);
}

/**
 * True when we already reloaded recently, meaning the fresh manifest also fails
 * to serve this chunk. Reloading again would loop, so let the error surface.
 */
function reloadedRecently(): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_KEY);
    if (!raw) return false;
    const at = Number(raw);
    return Number.isFinite(at) && Date.now() - at < RELOAD_COOLDOWN_MS;
  } catch {
    // Private mode or blocked storage: without a guard, reloading risks a loop.
    return true;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* storage unavailable — reloadedRecently() already fails closed */
  }
}

/**
 * Drop-in replacement for React.lazy that recovers from a stale chunk once.
 *
 * A restrição repete a do próprio `React.lazy` em vez de fixar
 * `ComponentType<unknown>`: props são posição contravariante, então a versão
 * estreita aceitava só componente sem props — e a gaveta da Vapt IA, que é
 * carregada sob demanda com props, não passava. Nada muda para as páginas de
 * rota, que continuam inferindo o mesmo tipo.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mesma assinatura de React.lazy
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const module = await factory();
      // Loaded fine: clear the guard so a future deploy can recover again.
      try {
        sessionStorage.removeItem(RELOAD_KEY);
      } catch {
        /* ignore */
      }
      return module;
    } catch (error) {
      if (!isChunkLoadError(error) || reloadedRecently()) throw error;

      markReloaded();
      window.location.reload();
      // Never settles: the document is being replaced.
      return new Promise<never>(() => {});
    }
  });
}
